/**
 * Past Due List Component
 * Displays a table of past-due accounts
 */

import { realUnitNumber } from '@resida/shared';

interface PastDueItem {
  leaseId: string;
  propertyName: string;
  unitNumber: string | null;
  address1: string;
  tenantName: string;
  tenantEmail: string;
  balance: number;
  daysOverdue: number;
  oldestUnpaidDate: string;
}

interface PastDueListProps {
  items: PastDueItem[];
}

export function PastDueList({ items }: PastDueListProps) {
  if (items.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <div className="text-5xl mb-2">✅</div>
        <p className="text-base m-0">
          No past-due accounts
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            <th className="p-3 text-left text-sm font-semibold text-foreground uppercase tracking-wide">
              Property
            </th>
            <th className="p-3 text-left text-sm font-semibold text-foreground uppercase tracking-wide">
              Unit
            </th>
            <th className="p-3 text-left text-sm font-semibold text-foreground uppercase tracking-wide">
              Tenant
            </th>
            <th className="p-3 text-right text-sm font-semibold text-foreground uppercase tracking-wide">
              Balance
            </th>
            <th className="p-3 text-right text-sm font-semibold text-foreground uppercase tracking-wide">
              Days Overdue
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.leaseId} className="border-b border-border">
              <td className="p-3 text-sm text-foreground">
                {item.propertyName}
              </td>
              <td className="p-3 text-sm text-foreground">
                {realUnitNumber(item.unitNumber) ?? '—'}
              </td>
              <td className="p-3 text-sm text-foreground">
                <div>{item.tenantName}</div>
                <div className="text-xs text-muted-foreground">
                  {item.tenantEmail}
                </div>
              </td>
              <td className="p-3 text-right text-sm font-semibold text-destructive">
                ${(item.balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="p-3 text-right text-sm text-foreground">
                <span className="inline-block px-2 py-1 bg-destructive/5 text-destructive rounded font-medium">
                  {item.daysOverdue} days
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
